"use client";

import {
  SearchableUserList,
  type SearchableUserListItem,
  SearchableUserListSkeleton,
} from "@/components/users/searchable-user-list";
import { useHiiveMembers } from "@/hooks/use-hiive-data";
import type { HiiveMemberListItem } from "./hiive-members.shared";

interface Props {
  hiiveId: number;
  initialMembers?: HiiveMemberListItem[];
}

export function HiiveMembersTabClient({ hiiveId, initialMembers }: Props) {
  const { data: members, isLoading } = useHiiveMembers(hiiveId, initialMembers);

  if (isLoading) {
    return <SearchableUserListSkeleton connectAppearance="solid" />;
  }

  const items: SearchableUserListItem[] = (members || []).map((member) => ({
    id: member.id,
    name: member.name,
    username: member.username,
    image: member.image,
    href:
      member.isBlocked || member.username.length === 0
        ? null
        : `/profile/${member.username}`,
    isBlocked: member.isBlocked,
    canConnect: !member.isYou,
    secondaryText: buildMemberSecondaryText(member),
    // The "Buddy" state is now the quick-add button's own resting state, so it
    // is no longer duplicated as a badge here.
    badges: member.isYou
      ? [
          {
            label: "You",
            variant: "outline" as const,
            className: "border-border/70 text-text-secondary",
          },
        ]
      : [],
  }));

  return (
    <SearchableUserList
      items={items}
      emptyMessage="No members yet."
      searchPlaceholder="Search members"
      connectSurface="hiive_members"
      // The filled brand button discover uses on its list rows, rather than
      // the glass pill — a solid fill is what reads on a flat surface, and
      // this roster is flat.
      connectAppearance="solid"
    />
  );
}

function buildMemberSecondaryText(member: {
  isBlocked?: boolean;
  username: string;
  mutualCount?: number;
}) {
  if (member.isBlocked) {
    return "Blocked profile";
  }

  const details: string[] = [];

  if (member.username) {
    details.push(`@${member.username}`);
  }

  if ((member.mutualCount ?? 0) > 0) {
    details.push(
      `${member.mutualCount} mutual${member.mutualCount === 1 ? "" : "s"}`,
    );
  }

  return details.join(" · ");
}
